<template>
  <div class="question-input">
    <div class="question-short">{{ question.short }}</div>
    <Composer
      v-if="question.long && workspaceId"
      class="question-long"
      :document="question.long"
      :workspace-id="workspaceId"
      :editable="false"
      :mentions-enabled="false"
    />

    <!-- Predefined choices -->
    <template v-if="hasChoices">
      <BaseInputSelect
        :multiple="isMulti"
        :value="singleValue"
        :values="multiValues"
        :options="options"
        select-only
        :placeholder="isMulti ? 'Select…' : 'Choose one…'"
        @update:value="onSingleSelect"
        @update:values="onMultiSelect"
      />
      <BaseInput
        v-if="showOther"
        display="form"
        has-styles
        :value="otherText"
        :is-one-line="false"
        :textarea-default-rows="2"
        :placeholder="otherPlaceholder"
        @update:value="otherText = $event"
      />
    </template>

    <!-- Free-text -->
    <template v-else>
      <template v-if="isMulti">
        <div v-for="(entry, i) in freeEntries" :key="i" class="free-entry-row">
          <BaseInput
            class="free-entry-input"
            display="form"
            has-styles
            :value="entry"
            :is-one-line="false"
            :textarea-default-rows="2"
            placeholder="Enter your answer…"
            @update:value="updateFreeEntry(i, $event)"
          />
          <BaseButton
            v-if="freeEntries.length > 1"
            kind="auxiliary"
            text="Remove"
            @click="removeFreeEntry(i)"
          />
        </div>
        <BaseButton kind="auxiliary" text="Add another" @click="addFreeEntry" />
      </template>
      <BaseInput
        v-else
        display="form"
        has-styles
        :value="freeEntries[0] ?? ''"
        :is-one-line="false"
        :textarea-default-rows="3"
        placeholder="Enter your answer…"
        @update:value="updateFreeEntry(0, $event)"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from "vue";

import type {
  AiAutomationRunQuestion,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import Composer from "@/components/editor/Composer.vue";

// Sentinel value for the synthetic "Other" option added to choice-based
// questions. Collides only with a real choice literally equal to this string.
const OTHER_VALUE = "__automation_run_other__";

export default defineComponent({
  name: "AutomationRunQuestionInput",
  components: { BaseButton, BaseInput, BaseInputSelect, Composer },
  props: {
    question: {
      type: Object as PropType<AiAutomationRunQuestion>,
      required: true,
    },
    workspaceId: {
      type: String as PropType<WorkspaceId>,
      default: undefined,
    },
  },
  emits: {
    "update:answers": (_: string[]) => true,
  },
  setup(props, { emit }) {
    const isMulti = computed(() => {
      const kind = props.question.answer_count.kind;
      return kind === "many" || kind === "any";
    });
    const hasChoices = computed(() => !!props.question.choices?.length);

    const options = computed<SelectOption[]>(() => [
      ...(props.question.choices ?? []).map((c) => ({
        value: c,
        label: c,
        aliases: [],
      })),
      { value: OTHER_VALUE, label: "Other…", aliases: [] },
    ]);

    // Choice state.
    const singleValue = ref("");
    const multiValues = ref<string[]>([]);
    const otherText = ref("");

    // Free-text state (always at least one row so a textarea is shown).
    const freeEntries = ref<string[]>([""]);

    // Multi-select: the note box is only for "Other" (its own answer, no
    // predefined value to fall back on). Single-select: it's shown as soon as
    // any choice is picked — optional context on a real choice, or the
    // mandatory answer itself when "Other" is picked.
    const showOther = computed(() =>
      isMulti.value
        ? multiValues.value.includes(OTHER_VALUE)
        : !!singleValue.value
    );

    const isSingleOtherSelected = computed(
      () => singleValue.value === OTHER_VALUE
    );

    const otherPlaceholder = computed(() =>
      isMulti.value || isSingleOtherSelected.value
        ? "Enter your answer…"
        : "Add a note (optional)…"
    );

    const onSingleSelect = (v: string) => {
      if (v !== singleValue.value) otherText.value = "";
      singleValue.value = v;
    };
    const onMultiSelect = (vs: string[]) => {
      multiValues.value = vs;
    };

    const updateFreeEntry = (i: number, v: string) => {
      freeEntries.value = freeEntries.value.map((e, idx) =>
        idx === i ? v : e
      );
    };
    const addFreeEntry = () => {
      freeEntries.value = [...freeEntries.value, ""];
    };
    const removeFreeEntry = (i: number) => {
      freeEntries.value = freeEntries.value.filter((_, idx) => idx !== i);
    };

    // The answer list for this question, derived from whichever widget is active.
    const answers = computed<string[]>(() => {
      if (hasChoices.value) {
        if (isMulti.value) {
          const concrete = multiValues.value.filter((v) => v !== OTHER_VALUE);
          const other = otherText.value.trim();
          if (multiValues.value.includes(OTHER_VALUE) && other) {
            concrete.push(other);
          }
          return concrete;
        }
        if (isSingleOtherSelected.value) {
          const other = otherText.value.trim();
          return other ? [other] : [];
        }
        if (!singleValue.value) return [];
        const note = otherText.value.trim();
        return [note ? `${singleValue.value} — ${note}` : singleValue.value];
      }
      if (isMulti.value) {
        return freeEntries.value.map((e) => e.trim()).filter(Boolean);
      }
      const first = freeEntries.value[0]?.trim();
      return first ? [first] : [];
    });

    watch(answers, (a) => emit("update:answers", a), { immediate: true });

    return {
      isMulti,
      hasChoices,
      options,
      singleValue,
      multiValues,
      otherText,
      freeEntries,
      showOther,
      otherPlaceholder,
      onSingleSelect,
      onMultiSelect,
      updateFreeEntry,
      addFreeEntry,
      removeFreeEntry,
    };
  },
});
</script>

<style scoped lang="scss">
.question-input {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.question-short {
  @extend %font-body-medium;

  @include themed() {
    color: t($primary);
  }
}

.question-long {
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-70);
  }
}

.free-entry-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  .free-entry-input {
    flex: 1;
    min-width: 0;
  }
}
</style>
